// apps/accounts/src/pages/admin/StaffAdvance.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffAdvance() {
  const [staffList, setStaffList] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ staff: '', amount: '', date: new Date().toISOString().split('T')[0], reason: '' });

  useEffect(() => { fetchStaff(); fetchAdvances(); }, []);

  const fetchStaff = async () => {
    try {
      const staff = await pb.collection('site_employees').getFullList({ expand: 'user', filter: 'isActive = true' });
      setStaffList(staff);
    } catch (err) { toast.error('Failed to load staff'); }
  };

  const fetchAdvances = async () => {
    try {
      const data = await pb.collection('staff_advance_payments').getFullList({ sort: '-created', expand: 'staff,staff.user' });
      setAdvances(data);
    } catch (err) { toast.error('Failed to load advances'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.staff || !form.amount) { toast.error('Select staff and enter amount'); return; }
    try {
      await pb.collection('staff_advance_payments').create({
        staff: form.staff,
        amount: parseFloat(form.amount),
        date: form.date,
        reason: form.reason || '',
        status: 'pending',
      });
      toast.success('Advance recorded');
      setForm({ staff: '', amount: '', date: new Date().toISOString().split('T')[0], reason: '' });
      fetchAdvances();
    } catch (err) { toast.error('Failed: ' + err.message); }
  };

  const markAdjusted = async (id) => {
    try {
      await pb.collection('staff_advance_payments').update(id, { status: 'adjusted' });
      toast.success('Marked as adjusted');
      fetchAdvances();
    } catch (err) { toast.error('Update failed'); }
  };

  const deleteAdvance = async (id) => {
    if (!window.confirm('Delete this advance record?')) return;
    try { await pb.collection('staff_advance_payments').delete(id); toast.success('Deleted'); fetchAdvances(); } 
    catch (err) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold">Advance Payments</h1>
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select value={form.staff} onChange={(e) => setForm({ ...form, staff: e.target.value })} className="px-3 py-2 border rounded-lg bg-white" required>
              <option value="">Select Staff</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.expand?.user?.name || s.id}</option>)}
            </select>
            <Input type="number" placeholder="Amount (?)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required min="1" step="0.01" />
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Input placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            <Button type="submit" className="col-span-full bg-amber-500 hover:bg-amber-600 text-white">
              <DollarSign className="w-4 h-4 mr-1" /> Add Advance
            </Button>
          </form>
        </CardContent>
      </Card>
      {loading ? <p className="text-center text-slate-500">Loading...</p> : advances.length === 0 ? <Card><CardContent className="p-8 text-center text-slate-500">No advance records</CardContent></Card> : 
      <div className="grid gap-4">
        {advances.map(a => (
          <Card key={a.id} className="border-l-4 border-l-amber-400">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-slate-800">{a.expand?.staff?.expand?.user?.name || 'Staff'}</p>
                <p className="text-sm text-slate-500">?{a.amount} � {new Date(a.date).toLocaleDateString()}</p>
                {a.reason && <p className="text-sm text-slate-600">?? {a.reason}</p>}
              </div>
              <div className="flex items-center gap-3">
                <Badge className={a.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                  {a.status === 'pending' ? '? Pending' : '? Adjusted'}
                </Badge>
                {a.status === 'pending' && (
                  <Button size="sm" variant="outline" className="border-green-500 text-green-600" onClick={() => markAdjusted(a.id)}>
                    <Check className="w-4 h-4 mr-1" /> Adjusted
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => deleteAdvance(a.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>}
    </div>
  );
}